import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Drawer, List, ListItem, ListItemIcon, ListItemText, Typography,
  AppBar, Toolbar, IconButton, Avatar, Divider, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, Chip, CircularProgress,
  Alert, Button, TextField, MenuItem, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  People as PeopleIcon,
  DashboardOutlined as DashboardIcon,
  LogoutOutlined as LogoutIcon,
  PersonOffOutlined as SuspendIcon,
  PersonOutlined as ActivateIcon,
  SearchOutlined as SearchIcon,
  Refresh as RefreshIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import api from '../../services/api';
import ChangePasswordDialog from '../Auth/ChangePassword';

const DRAWER_WIDTH = 240;

const ROLE_LABELS = {
  radiologist: 'Radiologist', clinician: 'Clinician', super_clinician: 'Super Clinician',
  technologist: 'Technologist', radiographer: 'Radiographer', sonographer: 'Sonographer',
  referring_physician: 'Referring Physician', nurse: 'Nurse',
  medical_records_officer: 'Medical Records Officer', facility_admin: 'Facility Admin',
  it_admin: 'IT Admin', compliance_officer: 'Compliance Officer',
};

function UsersView({ facilityId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (facilityId) params.append('facility', facilityId);
      const res = await api.get(`/users?${params}`);
      setUsers(res.data.users || res.data || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSuspend = async (userId) => {
    setActionLoading(userId + ':suspend');
    try {
      await api.patch(`/users/${userId}/suspend`);
      await fetchUsers();
    } catch { setError('Failed to suspend user'); }
    finally { setActionLoading(''); }
  };

  const handleActivate = async (userId) => {
    setActionLoading(userId + ':activate');
    try {
      await api.patch(`/users/${userId}/activate`);
      await fetchUsers();
    } catch { setError('Failed to activate user'); }
    finally { setActionLoading(''); }
  };

  const filtered = users.filter(u => {
    const matchesSearch = !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchesRole = !roleFilter || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 240 }}
        />
        <TextField select size="small" value={roleFilter} onChange={e => setRoleFilter(e.target.value)} sx={{ minWidth: 180 }} label="Filter by role">
          <MenuItem value="">All Roles</MenuItem>
          {Object.entries(ROLE_LABELS).map(([val, label]) => <MenuItem key={val} value={val}>{label}</MenuItem>)}
        </TextField>
        <Button startIcon={<RefreshIcon />} onClick={fetchUsers} size="small">Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(u => (
              <TableRow key={u._id} hover>
                <TableCell>{u.firstName} {u.lastName}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{ROLE_LABELS[u.role] || u.role}</TableCell>
                <TableCell>
                  <Chip
                    label={u.isActive ? 'Active' : 'Suspended'}
                    size="small"
                    color={u.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  {u.isActive ? (
                    <Tooltip title="Suspend user">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={['admin', 'facility_admin', 'it_admin'].includes(u.role) || actionLoading === u._id + ':suspend'}
                          onClick={() => handleSuspend(u._id)}
                        >
                          {actionLoading === u._id + ':suspend' ? <CircularProgress size={16} /> : <SuspendIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Activate user">
                      <IconButton
                        size="small"
                        color="success"
                        disabled={actionLoading === u._id + ':activate'}
                        onClick={() => handleActivate(u._id)}
                      >
                        {actionLoading === u._id + ':activate' ? <CircularProgress size={16} /> : <ActivateIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
        {filtered.length} of {users.length} users
      </Typography>
    </Box>
  );
}

function OverviewView({ user, onChangePassword, passwordChanged }) {
  return (
    <Box>
      {passwordChanged ? (
        <Alert severity="success" sx={{ mb: 3 }}>Password changed successfully.</Alert>
      ) : (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" startIcon={<LockResetIcon />} onClick={onChangePassword}>
              Change Now
            </Button>
          }
        >
          If you logged in with a temporary password, please change it immediately.
        </Alert>
      )}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>IT Administrator</Typography>
        <Typography variant="body2" color="text.secondary">
          Logged in as {user?.firstName} {user?.lastName} &middot; {user?.email}
        </Typography>
        {user?.facility?.facilityName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Facility: {user.facility.facilityName}
          </Typography>
        )}
      </Paper>
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Access Scope</Typography>
        <Typography variant="body2" color="text.secondary">
          As IT Administrator you can view and manage user accounts within your facility: suspend or re-activate staff accounts.
          Clinical data, patient records, and system configuration are outside this role's scope.
        </Typography>
      </Paper>
    </Box>
  );
}

const NAV_ITEMS = [
  { key: 'overview', label: 'Overview', icon: <DashboardIcon /> },
  { key: 'users', label: 'User Accounts', icon: <PeopleIcon /> },
];

export default function ItAdminDashboard() {
  const { user, logout } = useAuth();
  const { mode } = useThemeMode();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const theme = createTheme({
    palette: {
      mode,
      background: { default: mode === 'dark' ? '#0d1117' : '#f8fafc', paper: mode === 'dark' ? '#161b22' : '#ffffff' },
      primary: { main: '#1565c0' },
    },
  });

  const facilityId = user?.facility?._id || user?.facility;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0a1929' }}>
      <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', letterSpacing: 2, textTransform: 'uppercase', mb: 0.5 }}>
          DycoVue
        </Typography>
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
          IT Administration
        </Typography>
        {user?.facility?.facilityName && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mt: 0.5 }}>
            {user.facility.facilityName}
          </Typography>
        )}
      </Box>
      <List sx={{ flex: 1, pt: 1, px: 1 }}>
        {NAV_ITEMS.map(item => (
          <ListItem
            key={item.key}
            button
            onClick={() => { setActiveSection(item.key); setMobileOpen(false); }}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              color: activeSection === item.key ? '#fff' : 'rgba(255,255,255,0.55)',
              bgcolor: activeSection === item.key ? 'rgba(255,255,255,0.1)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: activeSection === item.key ? 600 : 400 }} />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: '#1565c0', fontSize: '0.85rem' }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }} noWrap>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }} noWrap>IT Admin</Typography>
          </Box>
        </Box>
        <Button
          fullWidth size="small" startIcon={<LockResetIcon />}
          onClick={() => setChangePasswordOpen(true)}
          sx={{ color: 'rgba(255,255,255,0.55)', justifyContent: 'flex-start', mb: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#fff' } }}
        >
          Change Password
        </Button>
        <Button
          fullWidth size="small" startIcon={<LogoutIcon />}
          onClick={logout}
          sx={{ color: 'rgba(255,255,255,0.55)', justifyContent: 'flex-start', '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#fff' } }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'users': return <UsersView facilityId={facilityId} />;
      default: return <OverviewView user={user} onChangePassword={() => setChangePasswordOpen(true)} passwordChanged={passwordChanged} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} onSuccess={() => setPasswordChanged(true)} />
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        {isMobile ? (
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}>
            {drawerContent}
          </Drawer>
        ) : (
          <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' } }}>
            {drawerContent}
          </Drawer>
        )}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {isMobile && (
            <AppBar position="static" elevation={0} sx={{ bgcolor: '#0a1929' }}>
              <Toolbar>
                <IconButton edge="start" color="inherit" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }}>
                  <MenuIcon />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>IT Administration</Typography>
              </Toolbar>
            </AppBar>
          )}
          <Box sx={{ flex: 1, p: { xs: 2, md: 4 }, overflow: 'auto' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {NAV_ITEMS.find(n => n.key === activeSection)?.label || 'Overview'}
            </Typography>
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
